import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import "./MissionResults.css";

const MissionResults = ({ user }) => {
    const { labId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const eventId = searchParams.get("event_id") || user?.event_id || null;
    const isOrganizer = user?.role === "organizer" || user?.role === "admin";

    const [results, setResults] = useState([]);
    const [selectedResult, setSelectedResult] = useState(null);
    const [summary, setSummary] = useState({ total_missions: 0, successful_missions: 0, failed_missions: 0, average_score: 0, highest_score: 0, total_teams: 0 });
    const [leaderboard, setLeaderboard] = useState([]);
    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState(isOrganizer ? "overview" : "history");

    useEffect(() => { loadData(); }, [labId, eventId, isOrganizer]);

    const loadData = async () => {
        setLoading(true);
        setError("");
        try {
            if (isOrganizer) await Promise.all([loadOrganizerResults(), loadLeaderboard()]);
            else await loadStudentResults();
        } catch (err) {
            console.error("MissionResults load error:", err);
            setError(err.message || "Unable to load results");
        } finally {
            setLoading(false);
        }
    };

    const loadStudentResults = async () => {
        const params = new URLSearchParams({ lab_id: labId });
        if (eventId) params.append("event_id", eventId);

        const response = await fetch(`/api/lab/results?${params}`, { credentials: "include" });
        const data = await response.json();

        if (!response.ok || !data.success) throw new Error(data.message || "Unable to load mission history");

        setResults(data.results || []);
        setTeam(data.team || null);
        if (data.summary) setSummary(data.summary);
    };

    const loadStudentResult = async id => {
        const params = new URLSearchParams();
        if (eventId) params.append("event_id", eventId);

        const query = params.toString();
        const url = query ? `/api/lab/results/${id}?${query}` : `/api/lab/results/${id}`;
        const response = await fetch(url, { credentials: "include" });
        const data = await response.json();

        if (!response.ok || !data.success) throw new Error(data.message || "Unable to load mission");

        setSelectedResult(data.result);
    };

    const loadOrganizerResults = async () => {
        const params = new URLSearchParams({ lab_id: labId });
        if (eventId) params.append("event_id", eventId);

        const response = await fetch(`/api/organizer/results?${params}`, { credentials: "include" });
        const data = await response.json();

        if (!response.ok || !data.success) throw new Error(data.message || "Unable to load organizer results");

        setResults(data.results || []);
        setSummary({
            total_missions: data.summary?.total_missions ?? 0,
            successful_missions: data.summary?.successful_missions ?? 0,
            failed_missions: data.summary?.failed_missions ?? 0,
            average_score: data.summary?.average_score ?? 0,
            highest_score: data.summary?.highest_score ?? 0,
            total_teams: data.summary?.total_teams ?? 0
        });
    };

    const loadLeaderboard = async () => {
        const params = new URLSearchParams({ lab_id: labId });
        if (eventId) params.append("event_id", eventId);

        const response = await fetch(`/api/organizer/leaderboard?${params}`, { credentials: "include" });
        const data = await response.json();

        if (!response.ok || !data.success) throw new Error(data.message || "Unable to load leaderboard");

        setLeaderboard(data.leaderboard || []);
    };

    const loadOrganizerResult = async id => {
        const params = new URLSearchParams();
        if (eventId) params.append("event_id", eventId);

        const query = params.toString();
        const url = query ? `/api/organizer/results/${id}?${query}` : `/api/organizer/results/${id}`;
        const response = await fetch(url, { credentials: "include" });
        const data = await response.json();

        if (!response.ok || !data.success) throw new Error(data.message || "Unable to load mission");

        setSelectedResult(data.result);
    };

    const viewResult = result =>
        isOrganizer ? loadOrganizerResult(result.id) : loadStudentResult(result.id);

    if (loading) {
        return (
            <div className="results-page">
                <div className="results-loading">Loading mission data...</div>
            </div>
        );
    }

    return (
        <div className="results-page">
            <div className="results-header">
                <div>
                    <div className="results-eyebrow">ASTROBEE HACKATHON</div>
                    <h1>{isOrganizer ? "Mission Operations" : "Mission Results"}</h1>
                    <p>
                        {isOrganizer
                            ? "Monitor missions, teams and competition performance."
                            : team
                                ? `${team.name || `Team ${team.id}`} mission history and performance.`
                                : "Review your Astrobee mission performance and history."}
                    </p>
                </div>
                <button className="back-button" onClick={() => navigate(`/labs/${labId}`)}>
                    Back to Lab
                </button>
            </div>

            {eventId && (
                <div className="event-banner">
                    <div><span>EVENT</span><strong>Astrobee Hackathon 2026</strong></div>
                    <div><span>EVENT ID</span><strong>{eventId}</strong></div>
                    {!isOrganizer && team && (
                        <div><span>MY TEAM</span><strong>{team.name || `Team ${team.id}`}</strong></div>
                    )}
                </div>
            )}

            {error && <div className="results-error">{error}</div>}

            {isOrganizer && (
                <div className="results-tabs">
                    {["overview", "history", "leaderboard"].map(tab => (
                        <button key={tab} className={activeTab === tab ? "active" : ""} onClick={() => setActiveTab(tab)}>
                            {tab === "overview" ? "Overview" : tab === "history" ? "Mission History" : "Leaderboard"}
                        </button>
                    ))}
                </div>
            )}

            {isOrganizer && activeTab === "overview" && (
                <>
                    <div className="metrics-grid">
                        <Metric label="Total Missions" value={summary.total_missions} />
                        <Metric label="Teams" value={summary.total_teams} />
                        <Metric label="Successful" value={summary.successful_missions} success />
                        <Metric label="Failed" value={summary.failed_missions} />
                        <Metric label="Average Score" value={formatScore(summary.average_score)} />
                        <Metric label="Highest Score" value={formatScore(summary.highest_score)} success />
                    </div>

                    <div className="panel">
                        <div className="panel-header">
                            <div>
                                <div className="section-eyebrow">RECENT ACTIVITY</div>
                                <h2>Recent Missions</h2>
                            </div>
                            <button className="secondary-button" onClick={() => setActiveTab("history")}>View All</button>
                        </div>
                        <MissionTable results={results.slice(0, 10)} organizer onView={viewResult} />
                    </div>
                </>
            )}

            {isOrganizer && activeTab === "history" && (
                <div className="panel">
                    <div className="panel-header">
                        <div>
                            <div className="section-eyebrow">ALL ATTEMPTS</div>
                            <h2>Mission History</h2>
                        </div>
                        <div className="mission-count">{results.length} missions</div>
                    </div>
                    <MissionTable results={results} organizer onView={viewResult} />
                </div>
            )}

            {isOrganizer && activeTab === "leaderboard" && (
                <div className="panel">
                    <div className="panel-header">
                        <div>
                            <div className="section-eyebrow">HACKATHON RANKINGS</div>
                            <h2>Team Leaderboard</h2>
                            <p className="panel-description">Teams ranked by their competition performance.</p>
                        </div>
                    </div>

                    {leaderboard.length === 0 ? (
                        <EmptyLeaderboard />
                    ) : (
                        <div className="leaderboard">
                            {leaderboard.map((team, index) => (
                                <div className="leader-row" key={team.team_id ?? team.id ?? index}>
                                    <div className="rank">#{index + 1}</div>
                                    <div className="team-info">
                                        <strong>
                                            {team.team_name || (team.team_id ? `Team ${team.team_id}` : "Team")}
                                        </strong>
                                        <span>{team.missions ?? team.total_missions ?? 0} missions</span>
                                    </div>
                                    <div className="leader-score">
                                        {formatScore(team.score ?? team.average_score ?? team.total_score)}
                                        <small>/100</small>
                                    </div>
                                    <div>
                                        {team.status && (
                                            <span className={`status-badge ${statusClass(team.status)}`}>
                                                {team.status}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!isOrganizer && team && (
                <div className="team-summary">
                    <div><span>MY TEAM</span><strong>{team.name || `Team ${team.id}`}</strong></div>
                    <div><span>TEAM MEMBERS</span><strong>{team.member_count ?? team.members?.length ?? "--"}</strong></div>
                    <div><span>MISSIONS</span><strong>{results.length}</strong></div>
                </div>
            )}

            {!isOrganizer && (
                <div className="panel">
                    <div className="panel-header">
                        <div>
                            <div className="section-eyebrow">TEAM EXPERIMENT HISTORY</div>
                            <h2>My Team Missions</h2>
                        </div>
                        <div className="mission-count">{results.length} missions</div>
                    </div>

                    {results.length === 0
                        ? <EmptyState />
                        : <MissionTable results={results} onView={viewResult} />}
                </div>
            )}

            {selectedResult && (
                <MissionDetail
                    result={selectedResult}
                    organizer={isOrganizer}
                    onClose={() => setSelectedResult(null)}
                />
            )}
        </div>
    );
};

const Metric = ({ label, value, success }) => (
    <div className="metric-card">
        <div className="metric-label">{label}</div>
        <div className={success ? "metric-value success-text" : "metric-value"}>{value}</div>
    </div>
);

const MissionTable = ({ results, organizer, onView }) => (
    <div className="history-table">
        <div className="table-header">
            {organizer && <div>TEAM / USER</div>}
            <div>MISSION</div>
            <div>STATUS</div>
            <div>SCORE</div>
            <div>TIME</div>
            <div>COLLISIONS</div>
            <div>DATE</div>
            <div></div>
        </div>

        {results.length === 0 ? (
            <div className="table-empty">No mission results found.</div>
        ) : (
            results.map(result => (
                <div className="table-row" key={result.id}>
                    {organizer && (
                        <div>
                            <strong>
                                {result.team_name ||
                                    (result.team_id ? `Team ${result.team_id}` : `User ${result.user_id}`)}
                            </strong>
                            <span>User ID: {result.user_id}</span>
                        </div>
                    )}

                    <div>
                        <strong>{result.mission_name}</strong>
                        <span>{result.mission_type || "Mission"}</span>
                    </div>

                    <div>
                        <span className={`status-badge ${statusClass(result.status)}`}>
                            {result.status}
                        </span>
                    </div>

                    <div className="score">
                        {result.score !== null && result.score !== undefined
                            ? `${formatScore(result.score)}/100`
                            : "--"}
                    </div>

                    <div>{formatTimeStatic(result.execution_time)}</div>
                    <div>{result.collisions ?? 0}</div>
                    <div>{formatDateStatic(result.created_at)}</div>

                    <div>
                        <button className="view-button" onClick={() => onView(result)}>View</button>
                    </div>
                </div>
            ))
        )}
    </div>
);

const MissionDetail = ({ result, organizer, onClose }) => (
    <div className="modal-backdrop">
        <div className="result-detail">
            <div className="detail-header">
                <div>
                    <div className="detail-label">{organizer ? "MISSION INSPECTION" : "MISSION RESULT"}</div>
                    <h2>{result.mission_name}</h2>
                </div>
                <button className="close-button" onClick={onClose}>Close</button>
            </div>

            <div className="metrics-grid">
                <Metric
                    label="Score"
                    value={result.score !== null && result.score !== undefined ? `${formatScore(result.score)}/100` : "--"}
                    success={result.score !== null && result.score !== undefined && result.score >= 80}
                />
                <Metric label="Status" value={result.status || "--"} success={result.status === "completed"} />
                <Metric label="Execution Time" value={formatTimeStatic(result.execution_time)} />
                <Metric label="Collisions" value={result.collisions ?? 0} />
                <Metric
                    label="Distance"
                    value={result.distance_traveled !== null && result.distance_traveled !== undefined ? `${result.distance_traveled} m` : "--"}
                />
                <Metric label="Code Version" value={result.code_version || "--"} />
            </div>

            <div className="detail-section">
                <h3>{organizer ? "Participant" : "Team"}</h3>
                <div className="info-grid">
                    <Info label="User ID" value={result.user_id ?? "--"} />
                    <Info label="Team" value={result.team_name || (result.team_id ? `Team ${result.team_id}` : "--")} />
                    <Info label="Event" value={result.event_id ?? "--"} />
                    <Info label="Lab" value={result.lab_id ?? "--"} />
                </div>
            </div>

            <div className="detail-section">
                <h3>Mission Information</h3>
                <div className="info-grid">
                    <Info label="Mission Type" value={result.mission_type || "--"} />
                    <Info label="Started" value={formatDateStatic(result.started_at)} />
                    <Info label="Completed" value={formatDateStatic(result.completed_at)} />
                    <Info label="Submitted" value={formatDateStatic(result.created_at)} />
                </div>
            </div>

            <div className="detail-section">
                <h3>Telemetry</h3>
                {result.telemetry ? (
                    <pre className="telemetry-data">{JSON.stringify(result.telemetry, null, 2)}</pre>
                ) : (
                    <div className="telemetry-placeholder">
                        <div>
                            <strong>Astrobee Telemetry</strong>
                            <span>No telemetry data available.</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    </div>
);

const Info = ({ label, value }) => (
    <div className="info-item">
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const EmptyState = () => (
    <div className="empty-state">
        <h3>No missions yet</h3>
        <p>Run your first Astrobee mission to see your team's results here.</p>
    </div>
);

const EmptyLeaderboard = () => (
    <div className="empty-state">
        <h3>No rankings yet</h3>
        <p>Team rankings will appear after missions are completed.</p>
    </div>
);

const statusClass = status => {
    switch (String(status || "").toLowerCase()) {
        case "completed":
        case "success":
        case "successful":
            return "success";
        case "failed":
        case "error":
            return "failed";
        case "cancelled":
        case "canceled":
            return "cancelled";
        case "running":
        case "queued":
        case "pending":
        default:
            return "running";
    }
};

const formatScore = score => {
    if (score === null || score === undefined || score === "") return "0";
    const number = Number(score);
    if (Number.isNaN(number)) return score;
    return number.toFixed(1).replace(".0", "");
};

const formatTimeStatic = seconds => {
    if (seconds === null || seconds === undefined || seconds === "") return "--";

    const totalSeconds = Number(seconds);
    if (Number.isNaN(totalSeconds)) return "--";

    const mins = Math.floor(totalSeconds / 60);
    const secs = Math.floor(totalSeconds % 60);

    return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const formatDateStatic = date => {
    if (!date) return "--";

    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return "--";

    return parsed.toLocaleString();
};

export default MissionResults;